/* Pure geometry for the SVG charts: scales, tick picking, paths. */

type ChartColor = "chart-1" | "chart-2" | "chart-3" | "chart-4" | "chart-5";

type ChartSeries = {
  key: string;
  label: string;
  color: ChartColor;
  values: (number | null)[];
  /**
   * Bars only: series that share a stack id stack in one column, and each
   * stack stands beside the others (a balance sheet's assets beside its
   * liabilities and equity). Without one, a series is its own column.
   */
  stack?: string;
};

/** The theme's chart colour for a series. Marks wear it; text never does. */
function chartColorVar(color: ChartColor): string {
  // The raw role, not `--color-chart-N`: `@theme inline` emits no such
  // custom property, so `var(--color-chart-N)` resolves to nothing.
  return `var(--nt-${color})`;
}

/** An id that is safe inside `url(#...)`, from a `useId()` and a series key. */
function safeId(...parts: string[]): string {
  return parts.join("-").replace(/[^a-zA-Z0-9_-]/g, "");
}

/** About `target` evenly spaced indexes, always the first and the last. */
function evenTicks(count: number, target = 6): number[] {
  if (count <= 0) return [];
  if (count <= target) return Array.from({ length: count }, (_, i) => i);
  const out = new Set<number>();
  for (let i = 0; i < target; i++) {
    out.add(Math.round((i * (count - 1)) / (target - 1)));
  }
  return [...out];
}

/** Every `step`th index so labels about `labelWidth` wide never touch. */
function bandTicks(count: number, bandWidth: number, labelWidth: number) {
  const step = Math.max(
    1,
    Math.ceil((labelWidth + 8) / Math.max(1, bandWidth)),
  );
  const out: number[] = [];
  for (let i = 0; i < count; i += step) out.push(i);
  return out;
}

/** The index whose position is nearest `x` among `count` evenly spread points. */
function nearestIndex(
  x: number,
  left: number,
  width: number,
  count: number,
  mode: "point" | "band",
): number {
  if (count <= 1 || width <= 0) return 0;
  const ratio = (x - left) / width;
  const raw = mode === "point" ? ratio * (count - 1) : ratio * count - 0.5;
  return Math.min(count - 1, Math.max(0, Math.round(raw)));
}

/** "M x y L x y" runs, broken wherever a value is missing; a lone point is a dot. */
function linePath(points: ({ x: number; y: number } | null)[]): string {
  let d = "";
  let run: { x: number; y: number }[] = [];
  const flush = () => {
    run.forEach((p, i) => {
      d += `${i === 0 ? "M" : "L"}${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
    });
    if (run.length === 1) d += "h0";
    run = [];
  };
  for (const p of points) {
    if (p) run.push(p);
    else flush();
  }
  flush();
  return d;
}

/** Closed shapes under each run of `linePath`, down to `baseline`. */
function areaPath(
  points: ({ x: number; y: number } | null)[],
  baseline: number,
): string {
  let d = "";
  let run: { x: number; y: number }[] = [];
  const flush = () => {
    const first = run[0];
    const last = run[run.length - 1];
    if (first && last) {
      d += `M${first.x.toFixed(2)} ${baseline.toFixed(2)}`;
      for (const p of run) d += `L${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
      d += `L${last.x.toFixed(2)} ${baseline.toFixed(2)}Z`;
    }
    run = [];
  };
  for (const p of points) {
    if (p) run.push(p);
    else flush();
  }
  flush();
  return d;
}

/**
 * Indexes of a daily series to label: the first day, then every seventh —
 * widened in whole weeks until no more than `maxLabels` fit, which is what
 * the chart passes from its measured width. A year of days in a 300px
 * column gets four labels, not fifty-two.
 */
function dailyTicks(count: number, maxLabels = Infinity): number[] {
  const cap = Math.max(1, Math.floor(maxLabels));
  let step = 7;
  while (Math.ceil(count / step) > cap) step += 7;
  const out: number[] = [];
  for (let i = 0; i < count; i += step) out.push(i);
  return out;
}

export {
  dailyTicks,
  areaPath,
  bandTicks,
  chartColorVar,
  evenTicks,
  linePath,
  nearestIndex,
  safeId,
};
export type { ChartColor, ChartSeries };
